import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
} from 'firebase/firestore';
import { Box, Paper, CircularProgress, Typography } from '@mui/material';
import GameList from './GameList';
import ReservationForm from './ReservationForm';
import { Game, BookingPeriod, GroupedGames, LotteryEntry } from '../types/types';
import { useLotteryOperations } from '../hooks/useLotteryOperations';

interface LotteryBookingViewProps {
  selectedBookingPeriod: BookingPeriod;
  games: GroupedGames;
  gamesById: { [gameId: number]: Game };
  loadingGames: boolean;
  tokenMap: { [gameId: number]: string };
  saveToken: (gameId: number, token: string, expiresAt: string) => void;
  removeToken: (gameId: number) => void;
  isAdmin: boolean;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const LotteryBookingView: React.FC<LotteryBookingViewProps> = ({
  selectedBookingPeriod,
  games,
  gamesById,
  loadingGames,
  tokenMap,
  saveToken,
  removeToken,
  isAdmin,
  onSuccess,
  onError,
}) => {
  const [loadingButtons, setLoadingButtons] = useState<{ [gameId: number]: boolean }>({});
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [lotteryEntries, setLotteryEntries] = useState<Record<number, LotteryEntry[]>>({});
  const [bookingPeriodOpen, setBookingPeriodOpen] = useState<boolean>(false);
  const [disabledGameIds, setDisabledGameIds] = useState<Set<number>>(new Set());

  const { createLotteryEntry, cancelLotteryEntry, cancelLotteryEntryById, setWinner, disableGame, enableGame } = useLotteryOperations();

  const startLoading = (gameId: number) => {
    setLoadingButtons((prev) => ({ ...prev, [gameId]: true }));
  };

  const stopLoading = (gameId: number) => {
    setLoadingButtons((prev) => ({ ...prev, [gameId]: false }));
  };

  useEffect(() => {
    const now = new Date();
    setBookingPeriodOpen(
      selectedBookingPeriod.booking_open_at.toDate() <= now &&
      selectedBookingPeriod.booking_close_at.toDate() > now
    );
  }, [selectedBookingPeriod]);

  // Listen to lottery entries in real-time
  useEffect(() => {
    const currentPeriodRef = doc(db, 'booking_periods', selectedBookingPeriod.id);

    const lotteryQuery = query(
      collection(db, 'public_lottery'),
      where('booking_period_id', '==', currentPeriodRef)
    );

    const unsubscribe = onSnapshot(lotteryQuery, (querySnapshot) => {
      const entries = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          game_id: data.game_id,
          person_name: data.person_name,
          status: data.status,
          created_at: data.created_at,
          booking_period_id: data.booking_period_id,
          day: data.day,
        };
      });

      // Group entries by game_id
      const groupedEntries = entries.reduce((acc, entry) => {
        if (!acc[entry.game_id]) {
          acc[entry.game_id] = [];
        }
        acc[entry.game_id].push(entry);
        return acc;
      }, {} as Record<number, LotteryEntry[]>);

      setLotteryEntries(groupedEntries);
    });

    return () => unsubscribe();
  }, [selectedBookingPeriod]);

  // Listen to disabled games in real-time
  useEffect(() => {
    const currentPeriodRef = doc(db, 'booking_periods', selectedBookingPeriod.id);

    const disabledQuery = query(
      collection(db, 'disabled_games'),
      where('booking_period_id', '==', currentPeriodRef)
    );

    const unsubscribe = onSnapshot(disabledQuery, (querySnapshot) => {
      const ids = new Set<number>();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.game_id) {
          ids.add(data.game_id);
        }
      });
      setDisabledGameIds(ids);
    });

    return () => unsubscribe();
  }, [selectedBookingPeriod]);

  const handleOpenLotteryForm = (gameId: number) => {
    const game = gamesById[gameId];
    if (!game) {
      onError('Valittua ottelua ei löydy.');
      return;
    }
    setSelectedGame(game);
    setOpenConfirm(true);
  };

  const handleCreateLotteryEntry = async (
    name: string,
    email: string,
    isPublicName: boolean
  ): Promise<{ success: boolean; message: string } | undefined> => {
    if (!selectedGame) {
      onError('Peliä tai varausjaksoa ei löytynyt.');
      return;
    }

    startLoading(selectedGame.id);

    try {
      await createLotteryEntry(selectedGame, selectedBookingPeriod, name, email, saveToken);
      onSuccess('Ilmoittautuminen arvontaan onnistui!');
      setOpenConfirm(false);
      setSelectedGame(null);
      return { success: true, message: 'Ilmoittautuminen arvontaan onnistui!' };
    } catch (err: any) {
      onError(err.message || 'Ilmoittautumisen luominen epäonnistui.');
      return { success: false, message: err.message || 'Ilmoittautumisen luominen epäonnistui.' };
    } finally {
      stopLoading(selectedGame.id);
    }
  };

  const handleCancelLotteryEntry = async (gameId: number) => {
    startLoading(gameId);

    const token = tokenMap[gameId];
    if (!token) {
      onError('No reservation token found for the selected game.');
      stopLoading(gameId);
      return;
    }

    try {
      await cancelLotteryEntry(gameId, token, removeToken);
      onSuccess('Reservation cancelled successfully.');
      stopLoading(gameId);
    } catch (err: any) {
      onError(err.message || "Failed to cancel reservation.");
      stopLoading(gameId);
    }
  };

  const handleCancelLotteryEntryById = async (entryId: string) => {
    try {
      await cancelLotteryEntryById(entryId, isAdmin);
      onSuccess('Osallistuminen peruttu.');
    } catch (err: any) {
      onError(err.message || 'Peruutus epäonnistui.');
    }
  };

  const handleSetWinner = async (entryId: string) => {
    try {
      await setWinner(entryId, isAdmin);
      onSuccess('Winner set successfully!');
    } catch (err: any) {
      onError(err.message || 'Failed to set winner.');
    }
  };

  const handleLotteryFormClose = () => {
    setSelectedGame(null);
    setOpenConfirm(false);
  };

  const handleToggleDisableGame = async (gameId: number, disabled: boolean) => {
    try {
      if (disabled) {
        await disableGame(gameId, selectedBookingPeriod.id);
      } else {
        await enableGame(gameId, selectedBookingPeriod.id);
      }
    } catch (err: any) {
      onError(err.message || 'Pelin tilan muuttaminen epäonnistui.');
    }
  };

  const GameBookingInfo = () => {
    if (!selectedBookingPeriod.infoText) return null;

    return (
      <Paper
        sx={{
          mt: 0,
          mb: 0,
          p: 3,
          bgcolor: "secondary.main",
          boxShadow: 1,
          width: "100%",
          maxWidth: "md",
          textAlign: "center",
        }}
      >
        <Typography variant="body1" gutterBottom sx={{ color: "text.primary" }}>
          {selectedBookingPeriod.infoText}
        </Typography>
      </Paper>
    );
  };

  return (
    <>
      <GameBookingInfo />
      <Box sx={{ width: "100%", maxWidth: "md", mt: 4 }}>
        <Paper elevation={3} sx={{ padding: 2, bgcolor: 'background.paper', color: 'primary.main' }}>
          {loadingGames ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <GameList
              games={games}
              days={{}}
              loadingButtons={loadingButtons}
              onReserve={handleOpenLotteryForm}
              onCancelInitial={() => {}}
              onCancelConfirmed={handleCancelLotteryEntry}
              onSetWinner={handleSetWinner}
              onCancelLotteryEntry={handleCancelLotteryEntryById}
              onToggleDisableGame={handleToggleDisableGame}
              bookingPeriodOpen={bookingPeriodOpen}
              tokenMap={tokenMap}
              isAdmin={isAdmin}
              mode="LOTTERY"
              lotteryEntries={lotteryEntries}
              disabledGameIds={disabledGameIds}
            />
          )}
        </Paper>
      </Box>

      {selectedGame && (
        <ReservationForm
          open={openConfirm}
          onClose={handleLotteryFormClose}
          onConfirm={handleCreateLotteryEntry}
          onCancel={handleLotteryFormClose}
          selectedGame={selectedGame}
          mode="LOTTERY"
        />
      )}
    </>
  );
};

export default LotteryBookingView;
